import type { ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PageHeader, Tabs, TabsContent, TabsList, TabsTrigger } from '@mkcore/ui';
import { useCan } from '@/lib/session';
import { ActivityTab, DocumentsTab, DuplicatesTab, ForgetTab, IndexTab, QuestionsTab, RunsTab, SourcesTab } from '@/features/knowledge/admin';

const TABS = ['sources', 'runs', 'documents', 'duplicates', 'forget', 'questions', 'activity', 'index'] as const;
const search = z.object({ tab: z.enum(TABS).optional() });

export const Route = createFileRoute('/_app/knowledge/admin')({ validateSearch: search, component: AdminPage });

/**
 * The management of the knowledge layer (CO-1127, CO-1131, CO-1143, CO-1118, CO-1162, CO-1088, CO-1151): sources with
 * their state, ingest runs, documents, items that look alike, forgetting with a preview, the question log, the activity
 * and the index. Each tab
 * shows what the caller may do; the server checks again.
 */
function AdminPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const manage = useCan('knowledge:manage');
  const remove = useCan('knowledge:delete');
  const tabs = TABS.filter((x) => (x === 'forget' ? remove : x === 'activity' || x === 'questions' || x === 'index' ? true : manage));
  const tab = s.tab && tabs.includes(s.tab) ? s.tab : tabs[0]!;
  const panels: Record<(typeof TABS)[number], () => ReactNode> = {
    sources: () => <SourcesTab />,
    runs: () => <RunsTab />,
    documents: () => <DocumentsTab />,
    duplicates: () => <DuplicatesTab />,
    forget: () => <ForgetTab />,
    questions: () => <QuestionsTab />,
    activity: () => <ActivityTab />,
    index: () => <IndexTab />,
  };
  return (
    <>
      <PageHeader title={t('knowledge.admin.title')} description={t('knowledge.admin.description')} />
      <Tabs value={tab} onValueChange={(v) => void navigate({ search: { tab: v as (typeof TABS)[number] }, replace: true })}>
        <TabsList>
          {tabs.map((x) => (
            <TabsTrigger key={x} value={x}>
              {t(`knowledge.admin.tabs.${x}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((x) => (
          <TabsContent key={x} value={x} className="pt-4">
            {tab === x && panels[x]()}
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
}
